import type { LabeledValuePayload } from '@Application/Users/UserStatuses/Actions/userStatusesFetched';

type DataFieldValue = string | number | boolean | LabeledValuePayload | readonly LabeledValuePayload[] | null;

type DataField = {
	readonly type: 'field';
	readonly path: string;
	readonly fieldType: { readonly value: string };
	readonly value: DataFieldValue;
	readonly options: readonly LabeledValuePayload[] | null;
};

type DataGroup = {
	readonly type: 'group';
	readonly entries: readonly DataEntry[];
};

type DataListItem = {
	readonly entries: readonly DataEntry[];
};

type DataList = {
	readonly type: 'list';
	readonly path: string;
	readonly items: readonly DataListItem[];
};

type DataEntry = DataField | DataGroup | DataList;

/** What the export data is built from: satisfied by the export response payload and by
 * `DocumentDetailView` alike. */
export type DocumentDataSource = {
	readonly sections: readonly { readonly entries: readonly DataEntry[] }[];
};

const isPlainRecord = (value: unknown): value is Record<string, unknown> => {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
};

/** Writes `value` at a dotted path inside `target`, creating the intermediate objects it needs — the
 * same nested-object shape `docx-kit-tag-contract` expects for a group's fields (`a.b.c` → `{ a: { b:
 * { c } } }`). Never touches an existing sibling key. */
const setAtPath = (target: Record<string, unknown>, path: string, value: unknown): void => {
	const segments = path.split('.');
	let cursor = target;

	segments.forEach((segment, index) => {
		if (index === segments.length - 1) {
			cursor[segment] = value;
			return;
		}

		const existing = cursor[segment];
		const nested: Record<string, unknown> = isPlainRecord(existing) ? existing : {};
		cursor[segment] = nested;
		cursor = nested;
	});
};

/** A list's own path is a strict prefix of every field path it carries — retrenching it turns an
 * absolute catalog path back into the key `docx-kit-tag-contract` expects inside one list element. */
const stripPrefix = (path: string, prefix: string): string => {
	return prefix !== '' && path.startsWith(prefix) ? path.slice(prefix.length) : path;
};

/** Orders the selected options exactly as the field declares them (`options`), appending any stored
 * value the catalog no longer lists at the end, in its stored order — never the order the user ticked
 * them in. */
const orderChoiceLabels = (
	selected: readonly LabeledValuePayload[],
	options: readonly LabeledValuePayload[] | null,
): string[] => {
	if (options === null) {
		return selected.map((entry) => entry.label);
	}

	const remaining = [...selected];
	const ordered: LabeledValuePayload[] = [];

	options.forEach((option) => {
		const index = remaining.findIndex((entry) => entry.value === option.value);

		if (index === -1) {
			return;
		}

		const [matched] = remaining.splice(index, 1);

		if (matched !== undefined) {
			ordered.push(matched);
		}
	});

	return [...ordered, ...remaining].map((entry) => entry.label);
};

/** TypeScript does not narrow a `readonly T[]` out of a union on the negative branch of
 * `Array.isArray` (a known limitation, already worked around the same way in `DocumentFieldView`) —
 * a dedicated type predicate replaces the runtime `Array.isArray` check for the object branch instead
 * of a cast. */
const isLabeledValue = (value: DataFieldValue): value is LabeledValuePayload => {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
};

/**
 * `renderTemplate` prints the data it receives verbatim, never translating an option code into its
 * label: this is where that translation happens instead. A single choice yields its label, a multiple
 * choice yields the list of its selected labels, ordered as the field declares its options. Returns
 * `undefined` to mean "omit this key" — an image field, a `null` value (a field never addressed, or a
 * value the server rejected) and an empty multiple choice all fall through to it, so the field simply
 * prints empty in the Word document.
 */
const resolveFieldValue = (field: DataField): unknown => {
	const { fieldType, value, options } = field;

	if (fieldType.value === 'image' || value === null) {
		return undefined;
	}

	if (Array.isArray(value)) {
		return value.length === 0 ? undefined : orderChoiceLabels(value, options);
	}

	if (isLabeledValue(value)) {
		return value.label;
	}

	return value;
};

const isScalarListItem = (item: DataListItem, listPath: string): boolean => {
	return (
		item.entries.length === 1 &&
		item.entries.some((entry) => entry.type === 'field' && entry.path === listPath)
	);
};

/** A scalar list carries exactly one field per item, whose path is the list's own path — no suffix,
 * hence no key to nest it under; a `null`/rejected value still occupies its slot in the array, it is
 * never dropped (unlike an omitted object key, dropping an array element would shift every later one). */
const resolveScalarListItemValue = (item: DataListItem): unknown => {
	const fieldEntry = item.entries.find((entry): entry is DataField => entry.type === 'field');

	if (fieldEntry === undefined) {
		return null;
	}

	return resolveFieldValue(fieldEntry) ?? null;
};

const buildListValue = (list: DataList): unknown[] => {
	if (list.items.length === 0) {
		return [];
	}

	if (list.items.every((item) => isScalarListItem(item, list.path))) {
		return list.items.map((item) => resolveScalarListItemValue(item));
	}

	const itemPrefix = `${list.path}.`;

	return list.items.map((item) => {
		const itemData: Record<string, unknown> = {};
		buildEntriesData(item.entries, itemData, itemPrefix);
		return itemData;
	});
};

/** Recurses through a section's (or a list item's) entries, writing fields at their path relative to
 * `pathPrefix` and flattening groups in place — a group contributes no nesting of its own beyond what
 * its fields' own absolute paths already carry, its `path`/`title` only ever label the screen. */
function buildEntriesData(
	entries: readonly DataEntry[],
	target: Record<string, unknown>,
	pathPrefix: string,
): void {
	entries.forEach((entry) => {
		if (entry.type === 'field') {
			const value = resolveFieldValue(entry);

			if (value !== undefined) {
				setAtPath(target, stripPrefix(entry.path, pathPrefix), value);
			}

			return;
		}

		if (entry.type === 'group') {
			buildEntriesData(entry.entries, target, pathPrefix);
			return;
		}

		setAtPath(target, stripPrefix(entry.path, pathPrefix), buildListValue(entry));
	});
}

/**
 * Builds the plain object `renderTemplate` fills a Word template with, from a document's own detail
 * response — nested by absolute catalog path, exactly as `docx-kit-tag-contract` expects
 * (`invoice.amount` → `{ invoice: { amount } }`, a list node → an array of such objects keyed by the
 * suffix past the list's own path).
 *
 * Guidance for whoever prepares a template against this data: every value a template author sees is
 * a label, never a stored code — write a condition against the label (`@field == "Appartement"`). A
 * multiple choice is a list of labels, so a checkbox is ticked with `"Mardi" in @field`; the right-hand
 * side of `in` must be a list, which is why a multiple choice is never joined into a single text.
 * Declare a multiple-answer question as a single field with the `multiple: true` facet, never as a
 * choice list.
 */
const buildDocumentExportData = (payload: DocumentDataSource): Record<string, unknown> => {
	const data: Record<string, unknown> = {};

	payload.sections.forEach((section) => {
		buildEntriesData(section.entries, data, '');
	});

	return data;
};

export default buildDocumentExportData;
